import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  SimpleGrid,
  Spinner,
  Badge,
  Icon,
  Divider,
  List,
  ListItem,
  ListIcon,
  Alert,
  AlertIcon,
  Code,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiAlertTriangle, FiDatabase, FiCloud, FiSettings } from 'react-icons/fi';

const API_BASE = '/api';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setHealth(data);
        setLastChecked(new Date());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'complete':
        return 'green';
      case 'degraded':
      case 'incomplete':
        return 'orange';
      case 'not_configured':
        return 'yellow';
      case 'error':
      case 'unhealthy':
        return 'red';
      default:
        return 'gray';
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'complete':
        return FiCheckCircle;
      case 'degraded':
      case 'incomplete':
      case 'not_configured':
        return FiAlertTriangle;
      case 'error':
      case 'unhealthy':
        return FiXCircle;
      default:
        return FiAlertTriangle;
    }
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  if (!health) {
    return (
      <Alert status="error">
        <AlertIcon />
        Failed to load health check data
      </Alert>
    );
  }

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and configuration status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="teal" onClick={fetchHealth} isLoading={loading}>
          Re-check
        </Button>
      </HStack>

      {/* Overall status banner */}
      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg"
        mb={6}
        py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
          <Text fontSize="sm" color="gray.600">
            Tenant: <Code>{health.tenant_id}</Code>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">
                {checks.database?.status || 'unknown'}
              </Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {checks.database.message}
              </Alert>
            )}
          </VStack>
        </Box>

        {/* Stripe API */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiCloud} boxSize={6} color="purple.500" />
            <Heading size="md">Stripe API</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.stripe_api?.status)} fontSize="sm">
                {checks.stripe_api?.status || 'unknown'}
              </Badge>
            </HStack>
            {checks.stripe_api?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.stripe_api.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.stripe_api?.account_id && (
              <>
                <HStack justify="space-between">
                  <Text color="gray.600">Account</Text>
                  <Code fontSize="sm">{checks.stripe_api.account_id}</Code>
                </HStack>
                {checks.stripe_api.account_name && (
                  <HStack justify="space-between">
                    <Text color="gray.600">Name</Text>
                    <Text fontWeight="medium">{checks.stripe_api.account_name}</Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <Text color="gray.600">Country</Text>
                  <Text>{checks.stripe_api.country}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Currency</Text>
                  <Badge variant="outline">{checks.stripe_api.default_currency?.toUpperCase()}</Badge>
                </HStack>
              </>
            )}
            {checks.stripe_api?.message && (
              <Alert status={checks.stripe_api.status === 'not_configured' ? 'warning' : 'error'} borderRadius="md" fontSize="sm">
                <AlertIcon />
                {checks.stripe_api.message}
              </Alert>
            )}
          </VStack>
        </Box>

        {/* Configuration */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiSettings} boxSize={6} color="orange.500" />
            <Heading size="md">Configuration</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.configuration?.status)} fontSize="sm">
                {checks.configuration?.status || 'unknown'}
              </Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total Settings</Text>
              <Text fontWeight="bold">{checks.configuration?.total_settings || 0}</Text>
            </HStack>
            {checks.configuration?.missing_required?.length > 0 && (
              <Box>
                <Text color="red.500" fontSize="sm" fontWeight="semibold" mb={1}>Missing Required:</Text>
                <List spacing={1}>
                  {checks.configuration.missing_required.map((k, i) => (
                    <ListItem key={i} fontSize="sm">
                      <ListIcon as={FiXCircle} color="red.400" />
                      <Code fontSize="xs">{k}</Code>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {checks.configuration?.missing_optional?.length > 0 && (
              <Box>
                <Text color="orange.500" fontSize="sm" fontWeight="semibold" mb={1}>Missing Optional:</Text>
                <List spacing={1}>
                  {checks.configuration.missing_optional.map((k, i) => (
                    <ListItem key={i} fontSize="sm">
                      <ListIcon as={FiAlertTriangle} color="orange.400" />
                      <Code fontSize="xs">{k}</Code>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
